import React, { useEffect, useState, useMemo } from 'react';
import { Calculator } from 'lucide-react';
import { fetchTeamShareRules } from '../../redux/api';
import { formatShareLabel } from '../../utils/teamShareUtils';

const LEVEL_ORDER = ['leader', 'senior', 'medior', 'junior', 'pomocni radnik'];

const sortRules = (rules) =>
  [...rules].sort((a, b) => {
    const rankA = LEVEL_ORDER.indexOf(a.higherLevel);
    const rankB = LEVEL_ORDER.indexOf(b.higherLevel);
    if (rankA !== rankB) return rankA - rankB;
    return LEVEL_ORDER.indexOf(a.lowerLevel) - LEVEL_ORDER.indexOf(b.lowerLevel);
  });

const ShareTable = ({ rules, className = '' }) => (
  <table className={`w-full text-xs ${className}`}>
    <thead>
      <tr className="text-gray-500 border-b border-gray-700/60">
        <th className="py-1 px-2 text-left font-medium">Viši nivo</th>
        <th className="py-1 px-2 text-left font-medium">Niži nivo</th>
        <th className="py-1 px-1.5 text-right font-medium w-12">Udeo 1</th>
        <th className="py-1 px-1.5 text-right font-medium w-12">Udeo 2</th>
      </tr>
    </thead>
    <tbody>
      {rules.map((rule) => (
        <tr
          key={`${rule.higherLevel}-${rule.lowerLevel}`}
          className="border-b border-gray-700/30 last:border-0 hover:bg-gray-900/40"
        >
          <td className="py-0.5 px-2 text-white whitespace-nowrap">
            {formatShareLabel(rule.higherLevel)}
          </td>
          <td className="py-0.5 px-2 text-gray-400 whitespace-nowrap">
            {formatShareLabel(rule.lowerLevel)}
          </td>
          <td className="py-0.5 px-1.5 text-right font-semibold text-purple-300 tabular-nums">
            {rule.higherSharePercent}%
          </td>
          <td className="py-0.5 px-1.5 text-right font-medium text-gray-400 tabular-nums">
            {rule.lowerSharePercent}%
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const TeamShareFormulaPanel = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTeamShareRules()
      .then(({ data }) => setRules(sortRules(data || [])))
      .catch(() => setRules([]))
      .finally(() => setLoading(false));
  }, []);

  const sortedRules = useMemo(() => sortRules(rules), [rules]);

  const { leftRules, rightRules } = useMemo(() => {
    const mid = Math.ceil(sortedRules.length / 2);
    return {
      leftRules: sortedRules.slice(0, mid),
      rightRules: sortedRules.slice(mid)
    };
  }, [sortedRules]);

  return (
    <div className="w-full mt-4 sm:mt-6 bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
      <div className="px-4 sm:px-4 py-3 sm:py-2.5 border-b border-gray-700 flex items-center gap-2">
        <Calculator size={16} className="text-purple-400 flex-shrink-0" />
        <h3 className="text-sm sm:text-base font-semibold text-white">
          Obračun udelа u timu
        </h3>
      </div>

      <div className="p-4 sm:p-3">
        {loading ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500" />
          </div>
        ) : sortedRules.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">
            Matrica udelа nije učitana.
          </p>
        ) : (
          <>
            <div className="hidden sm:grid sm:grid-cols-2 gap-x-4 rounded-lg border border-gray-700/80 bg-gray-900/40 px-1 py-1">
              <ShareTable rules={leftRules} />
              <ShareTable rules={rightRules} />
            </div>

            <div className="sm:hidden grid grid-cols-1 gap-2">
              {sortedRules.map((rule) => (
                <div
                  key={`${rule.higherLevel}-${rule.lowerLevel}`}
                  className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-gray-900 border border-gray-700"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white truncate">
                      {formatShareLabel(rule.higherLevel)}
                      <span className="text-gray-500 mx-1">—</span>
                      {formatShareLabel(rule.lowerLevel)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    <span className="text-sm font-bold text-purple-300">
                      {rule.higherSharePercent}%
                    </span>
                    <span className="text-gray-600">/</span>
                    <span className="text-sm font-medium text-gray-400">
                      {rule.lowerSharePercent}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TeamShareFormulaPanel;
